import {
  useRef,
  useState,
  useEffect,
  Dispatch,
  ReactNode,
  ChangeEvent,
  SetStateAction,
} from 'react'
import cn from 'clsx'
import {Link} from 'react-router-dom'
import {useDebouncedCallback} from 'use-debounce'
import {Input, MenuProps, Space} from 'antd'
import useStore from 'base/store'
import styles from './DocumentsContainer.module.scss'
import {
  Block,
  Text,
  Box,
  Button,
  Dropdown,
  FilterSecondIcon,
  FilterButton,
  SearchThirdIcon,
  UploadIcon,
  UserIcon,
  ArrowShortSecondIcon,
} from 'common/components'
import {useUploadFiles} from 'common/api'
import {maybePluralize} from 'common/helpers'
import {
  useAddDocuments,
  getDrawdownCollections,
  DOCUMENTS_CATEGORY,
  SORT_ITEMS,
  IDocumentsTab,
  TDocumentsCategory,
  TDocumentsModel,
  DOCUMENTS_COLLECTION,
} from '..'

interface DocumentsContainerProps {
  children: ReactNode
  categoryKey: TDocumentsCategory
  documentsCount?: number
  tabs?: IDocumentsTab[]
  activeTab?: IDocumentsTab
  modelUuid?: string
  modelType?: TDocumentsModel
  projectUuid?: string
  search?: string
  sort?: string
  setSearch?: Dispatch<SetStateAction<string>>
  setSort?: Dispatch<SetStateAction<string>>
  setActiveTab?: Dispatch<React.SetStateAction<IDocumentsTab>>
}

interface TabProps {
  activeTab: IDocumentsTab
  tab: IDocumentsTab
  number: string
  setActiveTab: Dispatch<React.SetStateAction<IDocumentsTab>>
}

interface SearchProps {
  showSearch: boolean
  setShowSearch: Dispatch<SetStateAction<boolean>>
}

interface UploadProps {
  modelUuid: string
  modelType: TDocumentsModel
  projectUuid?: string
  collection: string
}

interface UploadToDrawdownProps {
  modelUuid: string
  modelType: TDocumentsModel
  projectUuid?: string
  order: number
  activeTab: IDocumentsTab
}

interface SortProps {
  sort?: string
  setSort?: Dispatch<SetStateAction<string>>
}

export default function DocumentsContainer({
  children,
  categoryKey,
  documentsCount,
  tabs,
  activeTab,
  modelUuid,
  modelType,
  projectUuid,
  search,
  sort,
  setSearch,
  setSort,
  setActiveTab,
}: DocumentsContainerProps) {
  const role = useStore((state) => state.role)
  const [showSearch, setShowSearch] = useState(false)

  useEffect(() => {
    if (showSearch) return
    setSearch('')
  }, [showSearch]) // eslint-disable-line

  const isDrawdown = modelType === 'drawdown'

  const category = DOCUMENTS_CATEGORY[categoryKey]

  const hasUpload = Boolean(modelType) && activeTab?.collection !== 'documents' && (
    !role?.user
  )

  return (
    <Block className={cn(styles.root, tabs && styles.root_grid)}>
      {tabs && (
        <aside className={styles.sidebar}>
          <ul>
            {tabs.map((tab) =>
              tab.collection === DOCUMENTS_COLLECTION.documents.key &&
              !tab.filesCount ? null : (
                <Tab
                  key={tab.collection}
                  activeTab={activeTab}
                  tab={tab}
                  number={`${category.order}.${tab.order}`}
                  setActiveTab={setActiveTab}
                />
              )
            )}
          </ul>
        </aside>
      )}
      <div className={styles.content}>
        <div className={styles.panel}>
          <Space size={12}>
            <Text as="h3" weight="medium" size="3xl">
              {category.title
                ? maybePluralize(documentsCount, category.title)
                : category.label}
              {activeTab ? `/ ${activeTab.label}` : ''}
            </Text>
            {isDrawdown ? (
              <Link
                className={styles.link}
                tabIndex={-1}
                to={`/projects/${projectUuid}/drawdowns#${modelUuid}`}
              >
                <Button variant="secondary">view drawdown details</Button>
              </Link>
            ) : activeTab?.collection === 'documents' ? (
              <Link
                className={styles.link}
                tabIndex={-1}
                to={`/projects/${projectUuid}`}
              >
                <Button variant="secondary">view all conditions</Button>
              </Link>
            ) : null}
          </Space>
          <Space size={2}>
            <Search showSearch={showSearch} setShowSearch={setShowSearch} />
            {hasUpload &&
              (isDrawdown ? (
                <UploadToDrawdown
                  modelUuid={modelUuid}
                  modelType={modelType}
                  projectUuid={projectUuid}
                  order={category.order}
                  activeTab={activeTab}
                />
              ) : (
                <Upload
                  projectUuid={projectUuid}
                  modelUuid={modelUuid}
                  modelType={modelType}
                  collection={activeTab ? activeTab.collection : 'others'}
                />
              ))}
            {sort && <Sort sort={sort} setSort={setSort} />}
          </Space>
        </div>
        {showSearch && <SearchInput search={search} setSearch={setSearch} />}
        {children}
      </div>
    </Block>
  )
}

const Tab = ({activeTab, tab, number, setActiveTab}: TabProps) => {
  const handleClick = () => setActiveTab(tab)

  return (
    <li key={tab.collection}>
      <button
        className={cn(
          styles.tab,
          tab.collection === activeTab.collection && styles.tab_active
        )}
        onClick={handleClick}
      >
        <div className={styles.tabContent}>
          <span className={styles.number}>{number}</span>
          <span>{tab.label}</span>
          <Text
            noWrap
            as="span"
            color={tab.filesCount ? 'turquoizeDeep' : 'atmo'}
          >
            ・{tab.filesCount}
          </Text>
        </div>
        {tab.hasBorrowerDocument && <UserIcon className={styles.icon} />}
      </button>
    </li>
  )
}

const Search = ({showSearch, setShowSearch}: SearchProps) => {
  const handleShowSearchToggle = () => setShowSearch((state: boolean) => !state)

  return (
    <FilterButton
      active={showSearch}
      icon={<SearchThirdIcon />}
      onClick={handleShowSearchToggle}
    />
  )
}

const Sort = ({sort, setSort}: SortProps) => {
  const menu: MenuProps = {
    items: SORT_ITEMS,
    selectedKeys: [sort],
    onClick: ({key}) => setSort(key),
  }

  return (
    <Dropdown menu={menu}>
      <FilterButton icon={<FilterSecondIcon />}>Sort by: {sort}</FilterButton>
    </Dropdown>
  )
}

const Upload = ({
  projectUuid,
  modelUuid,
  modelType,
  collection,
}: UploadProps) => {
  const isAml = modelType === 'amlCheck'
  const {mutate: addDocuments, isMutating: isAddDocumentMutating} =
    useAddDocuments({
      modelUuid,
      modelType,
      collection: isAml ? 'other' : collection,
      projectUuid: isAml ? projectUuid : undefined,
    })
  const {mutate: uploadFiles, isMutating: isUploadFileMutating} =
    useUploadFiles({
      key: collection,
      attachFiles: (filesUuids) => {
        addDocuments(filesUuids)
      },
    })
  const inputRef = useRef(null)

  const handleClick = () => inputRef.current.click()

  const handleDownload = (e: ChangeEvent<HTMLInputElement>) => {
    uploadFiles(e.target.files)
    // clear the input value, there is no React specific way to do that
    setTimeout(() => {
      e.target.value = null
    })
  }

  return (
    <>
      <FilterButton
        icon={<UploadIcon />}
        loading={isUploadFileMutating || isAddDocumentMutating}
        onClick={handleClick}
      />
      <input
        multiple
        className={styles.uploadInput}
        type="file"
        ref={inputRef}
        onChange={handleDownload}
      />
    </>
  )
}

const UploadToDrawdown = ({
  modelUuid,
  modelType,
  projectUuid,
  order,
  activeTab,
}: UploadToDrawdownProps) => {
  const inputRef = useRef(null)
  const collectionRef = useRef(activeTab.collection)
  const {mutate: addDocuments, isMutating: isAddDocumentMutating} =
    useAddDocuments({
      modelUuid,
      modelType,
      projectUuid,
      collection: collectionRef.current,
    })
  const {mutate: uploadFile, isMutating: isUploadFileMutating} = useUploadFiles(
    {
      key: collectionRef.current,
      attachFiles: (filesUuids) => {
        addDocuments(filesUuids)
      },
    }
  )

  const handleDownload = (e: ChangeEvent<HTMLInputElement>) => {
    uploadFile(e.target.files)
    // clear the input value, there is no React specific way to do that
    setTimeout(() => {
      e.target.value = null
    })
  }

  const menu: MenuProps = {
    expandIcon: (
      <Box as="span" color="atmo">
        <ArrowShortSecondIcon />
      </Box>
    ),
    items: [
      {
        key: 'upload',
        label: 'Upload to...',
        type: 'group',
        children: getDrawdownCollections(order, activeTab.order),
      },
    ],
    onClick: ({key}) => {
      collectionRef.current = key
      inputRef.current.click()
    },
  }

  return (
    <>
      <Dropdown menu={menu}>
        <FilterButton
          icon={<UploadIcon />}
          loading={isUploadFileMutating || isAddDocumentMutating}
        />
      </Dropdown>
      <input
        multiple
        className={styles.uploadInput}
        type="file"
        ref={inputRef}
        onChange={handleDownload}
      />
    </>
  )
}

const SearchInput = ({search, setSearch}: any) => {
  const [value, setValue] = useState(search)

  const debouncedSetSearch = useDebouncedCallback(
    (value) => setSearch(value),
    300
  )

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
    debouncedSetSearch(e.target.value.trim())
  }

  return (
    <Box mb={24}>
      <Input
        autoFocus
        allowClear
        value={value}
        type="search"
        placeholder="Seacrh across the folder…"
        onChange={handleSearchChange}
      />
    </Box>
  )
}
